import { useState, useEffect, useCallback } from "react";
import {
  Experience,
  ExperienceStatus,
  markExperience,
} from "../lib/experiencesLib";

interface UseExperienceOptions {
  slug: string;
  experiences?: Experience[];
  onShow?: () => void;
  onComplete?: () => void;
  onDismiss?: () => void;
}

interface UseExperienceReturn {
  shouldShow: boolean;
  show: () => void;
  complete: () => Promise<void>;
  dismiss: () => Promise<void>;
}

/**
 * Hook to manage the lifecycle of a user experience
 * Automatically handles checking if an experience is assigned,
 * marking it as shown, and completing/dismissing it
 */
export const useExperience = ({
  slug,
  experiences,
  onShow,
  onComplete,
  onDismiss,
}: UseExperienceOptions): UseExperienceReturn => {
  const [shouldShow, setShouldShow] = useState(false);
  const [hasBeenShown, setHasBeenShown] = useState(false);
  const [currentExperience, setCurrentExperience] = useState<Experience | null>(
    null
  );

  useEffect(() => {
    const experience = experiences?.find(
      (exp) =>
        exp.slug === slug &&
        (exp.status === ExperienceStatus.ASSIGNED ||
          exp.status === ExperienceStatus.SHOWN)
    );

    if (experience && !hasBeenShown) {
      setCurrentExperience(experience);
      setShouldShow(true);
    }
  }, [slug, experiences, hasBeenShown]);

  const show = useCallback(() => {
    if (!hasBeenShown && currentExperience) {
      setHasBeenShown(true);
      markExperience(currentExperience.id, "shown").catch((error) => {
        console.error(`Failed to mark ${slug} experience as shown:`, error);
      });
      onShow?.();
    }
  }, [slug, hasBeenShown, currentExperience, onShow]);

  const complete = useCallback(async () => {
    setShouldShow(false);
    if (currentExperience) {
      try {
        await markExperience(currentExperience.id, "completed");
        onComplete?.();
      } catch (error) {
        console.error(`Failed to mark ${slug} experience as completed:`, error);
        throw error;
      }
    }
  }, [slug, currentExperience, onComplete]);

  const dismiss = useCallback(async () => {
    setShouldShow(false);
    if (currentExperience) {
      try {
        await markExperience(currentExperience.id, "dismissed");
        onDismiss?.();
      } catch (error) {
        console.error(`Failed to mark ${slug} experience as dismissed:`, error);
        throw error;
      }
    }
  }, [slug, currentExperience, onDismiss]);

  // Auto-show when shouldShow becomes true
  useEffect(() => {
    if (shouldShow && !hasBeenShown) {
      show();
    }
  }, [shouldShow, hasBeenShown, show]);

  return {
    shouldShow,
    show,
    complete,
    dismiss,
  };
};
